import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FolderPlus, Loader2, RefreshCcw, Trash2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { fmtDate } from "@/lib/constants";
import { PmBot } from "@/components/PmBot";

export default function Progetti() {
  const { projects, projectId, selectProject, reload } = useProject();
  const [nome, setNome] = useState("");
  const [cliente, setCliente] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [daTemplate, setDaTemplate] = useState(true);
  const [busy, setBusy] = useState("");

  const crea = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      const { data } = await api.post("/projects", { nome, cliente, descrizione, da_template: daTemplate });
      toast.success(`Progetto "${nome}" creato`);
      setNome("");
      setCliente("");
      setDescrizione("");
      await reload();
      selectProject(data.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy("");
  };

  const elimina = async (p) => {
    setBusy(p.id);
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success(`Progetto "${p.nome}" eliminato con tutti i dati collegati`);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy("");
  };

  const reset = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/projects/${p.id}/reset?da_template=true`);
      toast.success(`Progetto "${p.nome}" rigenerato dal template`);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy("");
  };

  return (
    <div className="space-y-6" data-testid="progetti-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Configurazione</p>
        <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Progetti</h1>
        <p className="mt-2 text-sm text-slate-600">
          Crea nuovi progetti con la stessa modalità operativa (sprint, milestone, catena Stefano Critelli → Majorbit →
          Cliente), rigenerali dal template o eliminali completamente.
        </p>
      </div>

      <form onSubmit={crea} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4"
            data-testid="create-project-form">
        <h3 className="text-lg font-semibold text-slate-900">Nuovo progetto</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nome progetto</Label>
            <Input required value={nome} onChange={(e) => setNome(e.target.value)} placeholder="es. RI-GENERA Fase 2"
                   className="mt-1.5 h-10" data-testid="new-project-name-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cliente</Label>
            <Input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="es. InfoCamere"
                   className="mt-1.5 h-10" data-testid="new-project-client-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Descrizione</Label>
            <Input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} placeholder="Breve descrizione"
                   className="mt-1.5 h-10" data-testid="new-project-description-input" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <Checkbox checked={daTemplate} onCheckedChange={(v) => setDaTemplate(!!v)}
                    data-testid="new-project-template-checkbox" />
          Precarica sprint, milestone e task dal template RI-GENERA
        </label>
        <Button type="submit" disabled={busy === "create"}
                className="h-10 bg-slate-900 text-white hover:bg-slate-800 hover:text-white transition-colors"
                data-testid="create-project-button">
          {busy === "create" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FolderPlus className="h-4 w-4 mr-2" />}
          Crea progetto
        </Button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(projects || []).map((p) => (
          <div key={p.id}
               className={`bg-white border rounded-xl p-5 shadow-sm ${p.id === projectId ? "border-slate-900" : "border-slate-200"}`}
               data-testid={`project-card-${p.nome}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900 truncate">{p.nome}</h3>
                <p className="text-sm text-slate-500">{p.cliente || "—"}</p>
                {p.descrizione && <p className="mt-1 text-sm text-slate-600">{p.descrizione}</p>}
                <p className="mt-2 font-mono text-[11px] text-slate-400">
                  Creato il {fmtDate(p.created_at)} da {p.created_by}
                </p>
              </div>
              {p.id === projectId && (
                <span className="shrink-0 flex items-center gap-1 font-mono text-[10px] px-2 py-1 rounded-full bg-slate-900 text-white">
                  <CheckCircle2 className="h-3 w-3" /> Attivo
                </span>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => selectProject(p.id)} disabled={p.id === projectId}
                      className="h-9 bg-white border-slate-300 hover:bg-slate-900 hover:text-white transition-colors"
                      data-testid={`select-project-${p.nome}`}>
                Seleziona
              </Button>
              <Button variant="outline" size="sm" onClick={() => reset(p)} disabled={busy === p.id}
                      className="h-9 bg-white border-slate-300 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors"
                      data-testid={`reset-project-${p.nome}`}>
                {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />}
                Rigenera dal template
              </Button>
              <Button variant="ghost" size="sm" onClick={() => elimina(p)} disabled={busy === p.id}
                      className="h-9 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                      data-testid={`delete-project-${p.nome}`}>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Elimina
              </Button>
            </div>
          </div>
        ))}
      </div>

      <PmBot pagina="dashboard" />

    </div>
  );
}
